import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { getPreSignedUrl } from 'src/redux/services/commonServices';
import { isValidUrl, getAcceptTypes } from '../../utils';
import './DocumentUpload.scss';
import { Spinner } from 'react-bootstrap';
import pdfIcon from 'src/assets/icons/pdf-icon.svg';
import redCrossIcon from 'src/assets/icons/redcross-icon.svg';
import { useDispatch } from 'react-redux';
import { setModalContent } from 'src/redux/modules/modalEvent';
import SkipUploadModal from './SkipUploadModal';
import AppButton from 'src/components/base/Button/Button';
import { Typography } from "src/latest/src/components";


interface props {
  questionData: any;
  onChange: (documentId: string | null, choiceId: string) => void;
  userDetails: any;
  supportData: any;
  fileMaxSize: number;
  documentType: any;
  handleOnclick: () => void;
}

const DocumentUpload: React.FC<props> = ({
  questionData,
  onChange,
  userDetails,
  supportData,
  fileMaxSize,
  documentType,
  handleOnclick,
}) => {
  const choice = questionData?.choices[0];

  const dispatch = useDispatch();

  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<any>({});
  const [uploadedDocumentSrc, setUploadedDocumentSrc] = useState<string>();
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFileTypeInvalid, setIsFileTypeInvalid] = useState<boolean>(false);
  const [isDocumentUploaded, setIsDocumentUploaded] = useState<boolean>(false);

  useEffect(() => {
    if (!isDocumentUploaded && choice?.value && isValidUrl(choice?.metadata?.signed_url)) {
      setUploadedDocumentSrc(choice?.metadata?.signed_url);
      setUploadedFileName(choice?.metadata?.file_name);
    }
  }, [choice, isDocumentUploaded]);

  useEffect(() => {
    if (userDetails) {
      setUser(userDetails);
    }
  }, [userDetails]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setError(null);
      setIsFileTypeInvalid(false);
      acceptedFiles.forEach(file => onUploadDocument(file));
    },
    [user],
  );

  const onDropRejected = useCallback(
    rejectedFiles => {
      rejectedFiles.forEach((file: any) => {
        if (file.errors.some((error: any) => error.code === 'file-too-large')) {
          setError(`"${file.file.name}" exceeds the size limit of 5MB.`);
        } else {
          setError('Only PDF files are allowed.');
        }
      });
      setIsFileTypeInvalid(true);
    },
    [isFileTypeInvalid],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected,
    accept: getAcceptTypes(supportData),
    maxSize: fileMaxSize, // Max file size: 5MB
  });

  const onUploadDocument = async (file: any) => {
    setIsLoading(true);
    setIsFileTypeInvalid(true);

    try {
      const preSignedUrl = await getPreSignedUrl(
        documentType,
        file,
        user?.id,
        user?.id,
        false,
      );

      if (preSignedUrl) {
        setUploadedDocumentSrc(decodeURIComponent(preSignedUrl.signed_url));
        setUploadedFileName(file.name);
        setIsDocumentUploaded(true);
        onChange(preSignedUrl.id, choice.id);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to upload document. Please try again.');
    } finally {
      setIsLoading(false);
      setIsFileTypeInvalid(false);
    }
  };

  const onRemoveDocument = () => {
    setUploadedDocumentSrc(undefined);
    setUploadedFileName(null);
    setError(null);
    setIsFileTypeInvalid(false);
    setIsDocumentUploaded(false);
    onChange(null, choice.id);
  };

  const handleContinueclick = () => {
    dispatch(
      setModalContent({
        modalContent: <SkipUploadModal handleOnclick={handleOnclick} />,
        isWithCancelButton: false,
        size: 'lg',
        title: 'Skip Consent Document Upload',
        className: 'skip-upload-modal',
      }),
    );
  };

  const handleButtonClick = () => {
    if (!uploadedDocumentSrc) {
      handleContinueclick();
    } else {
      handleOnclick();
    }
  };

  return (
    <div className='mt-3 document-upload-container'>
      {isLoading ? (
        <div className='spinner-container'>
          <Spinner animation='border' role='status' />
        </div>
      ) : !uploadedDocumentSrc ? (
        <div className='dropzone_parent_container mb-3'>
          <div {...getRootProps()} className='dropzone_container'>
            <input {...getInputProps()} />
            <Typography
              className='doc-inner-text'
              weight='regular'
              variant='size-18'
            >
              Drag & drop the PDF here <br /> or click to select PDF <br /> (Optional)
            </Typography>
          </div>
        </div>
      ) : (
        <>
          <div>
            <div className='dropzone_parent_container mb-3'>
              <div className='file-name-container text-center'>
                <div className='cancel-icon'>
                  <button className='btn' onClick={onRemoveDocument}>
                    <div>
                      <img src={redCrossIcon} alt='cancel-icon' />
                    </div>
                  </button>
                </div>
                <div className='uploaded-file-name'>
                  <div className='mb-1'>
                    <img src={pdfIcon} alt='PDF' />
                  </div>
                  <Typography
                    variant='size-18'
                    weight='semi-bold'
                    className='mt-2 file-name-text'
                  >
                    {uploadedFileName}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {error && (
        <div className='error_div text-center text-danger'>{error}</div>
      )}
      <div className='d-flex justify-content-center mt-5 mb-4'>
        <AppButton
          disabled={isFileTypeInvalid || isLoading}
          label='Continue'
          onClick={handleButtonClick}
          className='confirm-btn mt-4'
        />
      </div>
    </div>
  );
};

export default DocumentUpload;
